import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";

const API_URL = import.meta.env.VITE_API_URL;

export interface DispatchTimeWindow {
  /** "HH:mm" — 24h, no seconds. Sortable lex. */
  startTime: string;
  endTime: string;
  /** Slot ids that share this (startTime, endTime). The task-list filter
   *  matches a task's scheduledWindow against the (startTime, endTime),
   *  not against a slot id — slot ids vary per (service, dispatch), but
   *  the window is a single dropdown choice for the dispatcher. */
  slotIds: string[];
}

/**
 * Unique pickup-slot time windows for the admin dispatch screen's window
 * filter, aggregated across the selected carriers' PICKUP services + (optionally)
 * dispatch centers. Backed by `GET /api/v2/admin/dispatch/time-windows`.
 *
 * Empty `carrierIds` → no fetch, returns `[]` immediately. Matches the
 * carrier-filter's "empty = nothing" semantic.
 */
export function useDispatchTimeWindows(
  carrierIds: string[],
  dispatchCenterIds: string[]
) {
  const { token } = useSession();
  const carrierIdsParam = carrierIds.join(",");
  const dispatchCenterIdsParam = dispatchCenterIds.join(",");

  return useQuery({
    queryKey: ["dispatchTimeWindows", carrierIdsParam, dispatchCenterIdsParam],
    queryFn: async (): Promise<DispatchTimeWindow[]> => {
      if (carrierIdsParam.length === 0) return [];
      const { data } = await axios.get<{
        success: boolean;
        data: DispatchTimeWindow[];
      }>(`${API_URL}/api/v2/admin/dispatch/time-windows`, {
        params: {
          carrierIds: carrierIdsParam,
          ...(dispatchCenterIdsParam.length > 0 && {
            dispatchCenterIds: dispatchCenterIdsParam,
          }),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    enabled: !!token,
    staleTime: 60_000,
  });
}
